import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { IoLogoPaypal } from "react-icons/io5";
import { ACTION_TYPES } from "../../Constants";
import { hideLoader, showLoader } from "../../Redux/Loader/actions";
import useLewooPayWalletAccess from "../../Hooks/useLewooPayWalletAccess";
import WalletApi from "../../Services/Wallet";
import "./HomePage.css";

const WALLET_COLORS = ["#0b7a45", "#f97316", "#2457f3", "#ec4899"];

function formatFCFA(amount) {
  const num = parseFloat(amount) || 0;
  return num.toLocaleString("fr-FR").replace(/ /g, " ");
}

function getWalletDisplayName(wallet, intl) {
  if (wallet.is_primary) return intl.formatMessage({ id: "dashboard.walletPage.walletName.primary" });
  return intl.formatMessage({ id: "dashboard.walletPage.walletName.other" }, { currency: wallet.currency });
}

const MONTH_NAMES = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

function formatDateArr(dateArr) {
  if (!Array.isArray(dateArr) || dateArr.length < 3) return "";
  const [year, month, day] = dateArr;
  return `${day} ${MONTH_NAMES[month - 1]} ${year}`;
}

function normalizeTransaction(t, intl) {
  const isDebit = t.debit === true || t.entryType === "DEBIT";
  const paymentName = t.paymentDetailData?.paymentType?.name || "";
  const currency = t.currency?.code || "FCFA";
  return {
    id: t.id,
    icon: isDebit ? "down" : "up",
    title: isDebit
      ? (paymentName
          ? intl.formatMessage({ id: "dashboard.walletPage.transaction.withdrawWithMethod" }, { method: paymentName })
          : intl.formatMessage({ id: "dashboard.walletPage.transaction.withdraw" }))
      : (paymentName
          ? intl.formatMessage({ id: "dashboard.walletPage.transaction.rechargeWithMethod" }, { method: paymentName })
          : intl.formatMessage({ id: "dashboard.walletPage.transaction.recharge" })),
    route: paymentName || "",
    date: formatDateArr(t.date),
    time: "",
    kind: isDebit
      ? intl.formatMessage({ id: "dashboard.walletPage.transaction.withdraw" })
      : intl.formatMessage({ id: "dashboard.walletPage.transaction.recharge" }),
    amount: (isDebit ? "- " : "+ ") + formatFCFA(t.amount),
    balance: intl.formatMessage(
      { id: "dashboard.walletPage.transaction.balance" },
      { value: `${formatFCFA(t.runningBalance)} ${currency}` }
    ),
    tone: isDebit ? "danger" : "success",
  };
}

function getQuickActions(intl) {
  return [
    {
      title: intl.formatMessage({ id: "dashboard.walletPage.quickActions.recharge.title" }),
      desc: intl.formatMessage({ id: "dashboard.walletPage.quickActions.recharge.desc" }),
    },
    {
      title: intl.formatMessage({ id: "dashboard.walletPage.quickActions.withdraw.title" }),
      desc: intl.formatMessage({ id: "dashboard.walletPage.quickActions.withdraw.desc" }),
    },
    {
      title: intl.formatMessage({ id: "dashboard.walletPage.quickActions.methods.title" }),
      desc: intl.formatMessage({ id: "dashboard.walletPage.quickActions.methods.desc" }),
    },
    {
      title: intl.formatMessage({ id: "dashboard.walletPage.quickActions.history.title" }),
      desc: intl.formatMessage({ id: "dashboard.walletPage.quickActions.history.desc" }),
    },
  ];
}

const PM_BADGE = { visa: "VISA", mastercard: "MC", amex: "AMEX", mtn: "MTN", orange: "OM" };
const PM_TONE = { visa: "blue", mastercard: "orange", amex: "purple", mtn: "yellow", orange: "orange" };
const PM_NAME = { visa: "Visa", mastercard: "Mastercard", amex: "Amex", mtn: "MTN Mobile Money", orange: "Orange Money" };

function normalizePaymentMethod(pm) {
  const key = (pm.brand || pm.type || "").toLowerCase();
  const badge = PM_BADGE[key] || key.toUpperCase().slice(0, 4);
  const tone = PM_TONE[key] || "blue";
  const displayName = PM_NAME[key] || pm.bank_name || key;

  if (pm.type === "card" && pm.last_four) {
    const expM = String(pm.exp_month || 1).padStart(2, "0");
    const expY = String(pm.exp_year || "").slice(-2);
    return { id: pm.id, badge, tone, brand: `${displayName} •••• ${pm.last_four}`, number: `Expire ${expM}/${expY}`, is_default: pm.is_default };
  }
  return { id: pm.id, badge, tone, brand: displayName, number: pm.account_last4 ? `•••• ${pm.account_last4}` : "", is_default: pm.is_default };
}

const RECHARGE_PRESET_AMOUNTS = [5000, 10000, 25000, 50000, 100000, 200000];

function getRechargePaymentMethods(intl) {
  return [
    {
      id: "orange",
      tone: "orange",
      badge: "orange",
      title: intl.formatMessage({ id: "dashboard.walletPage.recharge.orange.title" }),
      description: intl.formatMessage({ id: "dashboard.walletPage.recharge.orange.description" }),
      helper: "N° : +237 XXX XXX XXX",
    },
    {
      id: "mtn",
      tone: "yellow",
      badge: "MTN",
      title: intl.formatMessage({ id: "dashboard.walletPage.recharge.mtn.title" }),
      description: intl.formatMessage({ id: "dashboard.walletPage.recharge.mtn.description" }),
      helper: "N° : +237 XXX XXX XXX",
    },
    {
      id: "paypal",
      tone: "blue",
      badge: "PP",
      title: intl.formatMessage({ id: "dashboard.walletPage.recharge.paypal.title" }),
      description: intl.formatMessage({ id: "dashboard.walletPage.recharge.paypal.description" }),
      helper: "user@email.com",
    },
  ];
}

function parseRechargeAmount(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? parseInt(digits, 10) : 0;
}

function formatRechargeAmountInput(value) {
  const amount = parseRechargeAmount(value);
  return amount ? amount.toLocaleString("fr-FR").replace(/ /g, " ") : "";
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 12s3.8-6 9.5-6 9.5 6 9.5 6-3.8 6-9.5 6-9.5-6-9.5-6Z" />
      <circle cx="12" cy="12" r="2.5" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="10" width="14" height="10" rx="2.5" />
      <path d="M8.5 10V8.5a3.5 3.5 0 1 1 7 0V10" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

function CardIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="6" width="16" height="12" rx="2.5" />
      <path d="M4 10h16" />
    </svg>
  );
}

function WalletMiniIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.5 8h12.2a2.3 2.3 0 0 1 2.3 2.3v5.4a2.3 2.3 0 0 1-2.3 2.3H6.8a2.3 2.3 0 0 1-2.3-2.3V8Z" />
      <path d="M17 11.3h3.2v4.4H17" />
      <path d="M7.2 12.4h3.1" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 4v5c0 4.5-3 7.7-7 9-4-1.3-7-4.5-7-9V7l7-4Z" />
      <path d="M9.2 12.1l1.8 1.8 3.8-3.8" />
    </svg>
  );
}

function OrangeMoneyLogo() {
  return (
    <svg viewBox="0 0 72 48" aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="71" height="47" rx="12" fill="#ff8a1d" />
      <text
        x="36"
        y="28"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="13"
        fontWeight="800"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        orange
      </text>
    </svg>
  );
}

function MtnMoneyLogo() {
  return (
    <svg viewBox="0 0 72 48" aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="71" height="47" rx="12" fill="#ffe55a" />
      <text
        x="36"
        y="28"
        textAnchor="middle"
        fill="#201d14"
        fontSize="14"
        fontWeight="900"
        fontFamily="Arial, Helvetica, sans-serif"
      >
        MTN
      </text>
    </svg>
  );
}

function PaypalLogo() {
  return <IoLogoPaypal aria-hidden="true" focusable="false" />;
}

function TransactionIcon({ icon }) {
  return icon === "up" ? (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 14l6-6 6 6" />
      <path d="M12 8v12" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 10l6 6 6-6" />
      <path d="M12 4v12" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12l5 5L19 7" />
    </svg>
  );
}

function WalletDropdown({ wallets, selectedId, onSelect, onClose, onCreateWallet, triggerRef }) {
  const intl = useIntl();
  const panelRef = useRef(null);

  useEffect(() => {
    function handlePointerDown(e) {
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      const inTrigger = triggerRef && triggerRef.current && triggerRef.current.contains(e.target);
      if (!inPanel && !inTrigger) {
        onClose();
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [onClose, triggerRef]);

  return (
    <div className="wc-wallet-dropdown" ref={panelRef} role="listbox" aria-label={intl.formatMessage({ id: "dashboard.walletPage.dropdown.ariaLabel" })}>
      <span className="wc-wallet-dropdown-header">{intl.formatMessage({ id: "dashboard.walletPage.dropdown.header" })}</span>
      <div className="wc-wallet-dropdown-list">
        {wallets.length === 0 && (
          <p className="wc-wallet-dropdown-empty">{intl.formatMessage({ id: "dashboard.walletPage.dropdown.empty" })}</p>
        )}
        {wallets.map((w) => (
          <button
            key={w.id}
            type="button"
            role="option"
            aria-selected={w.id === selectedId}
            className={`wc-wallet-dropdown-item${w.id === selectedId ? " is-selected" : ""}`}
            onClick={() => { onSelect(w.id); onClose(); }}
          >
            <span className="wc-wallet-dropdown-icon" style={{ background: w.color }} aria-hidden="true" />
            <span className="wc-wallet-dropdown-name">{w.name}</span>
            <span className="wc-wallet-dropdown-bal">{w.balanceFmt} {w.currency}</span>
            {w.id === selectedId ? <CheckIcon /> : <span className="wc-wallet-dropdown-spacer" />}
          </button>
        ))}
      </div>
      <button type="button" className="wc-wallet-dropdown-create" onClick={onCreateWallet}>
        <PlusIcon />
        {intl.formatMessage({ id: "dashboard.walletPage.dropdown.create" })}
      </button>
    </div>
  );
}

function WalletTopBar({ wallet, wallets, showBalance, isSyncing, onToggleBalance, onOpenDropdown, dropdownOpen, onSelect, onClose, onCreateWallet, loading }) {
  const intl = useIntl();
  const triggerRef = useRef(null);
  const eurEquiv = wallet ? (wallet.balance / 655.957).toFixed(2).replace(".", ",") : "0,00";

  return (
    <article className="wc-wallet-topbar">
      <div className="wc-wallet-topbar-left">
        <span className="wc-wallet-topbar-label">{intl.formatMessage({ id: "dashboard.walletPage.topbar.activeWallet" })}</span>
        <div className="wc-wallet-topbar-selector-wrap">
          {loading ? (
            <div className="wc-wallet-topbar-skeleton" />
          ) : (
            <button
              type="button"
              className="wc-wallet-selector-btn"
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
              onClick={onOpenDropdown}
              ref={triggerRef}
              disabled={!wallet}
            >
              {wallet ? (
                <>
                  <span className="wc-wallet-selector-icon" style={{ background: wallet.color }} aria-hidden="true" />
                  <span className="wc-wallet-selector-name">{wallet.name}</span>
                </>
              ) : (
                <span className="wc-wallet-selector-name">{intl.formatMessage({ id: "dashboard.walletPage.topbar.noWallet" })}</span>
              )}
              <ChevronDownIcon />
            </button>
          )}
          {dropdownOpen && wallet && (
            <WalletDropdown
              wallets={wallets}
              selectedId={wallet.id}
              onSelect={onSelect}
              onClose={onClose}
              onCreateWallet={onCreateWallet}
              triggerRef={triggerRef}
            />
          )}
        </div>
      </div>

      <div className="wc-wallet-topbar-center">
        <span className="wc-wallet-topbar-label">{intl.formatMessage({ id: "dashboard.walletPage.topbar.availableBalance" })}</span>
        <div className="wc-wallet-topbar-balance-row">
          {loading || isSyncing ? (
            <div className="wc-wallet-topbar-skeleton wc-wallet-topbar-skeleton--balance" />
          ) : (
            <strong className="wc-wallet-topbar-amount">
              {showBalance ? (wallet ? wallet.balanceFmt : "0") : "• • • •"}{" "}
              <span>{wallet ? wallet.currency : "FCFA"}</span>
            </strong>
          )}
          <button
            type="button"
            className="wc-wallet-topbar-eye"
            aria-label={intl.formatMessage({ id: showBalance ? "dashboard.walletPage.topbar.hideBalance" : "dashboard.walletPage.topbar.showBalance" })}
            onClick={onToggleBalance}
          >
            <EyeIcon />
          </button>
        </div>
        <small className="wc-wallet-topbar-eur">
          {loading || isSyncing ? "" : showBalance && wallet ? `≈ ${eurEquiv} €` : "—"}
        </small>
      </div>

      <div className="wc-wallet-topbar-right">
        <button type="button" className="wc-wallet-topbar-create-btn" onClick={onCreateWallet}>
          <PlusIcon />
          {intl.formatMessage({ id: "dashboard.walletPage.topbar.createWallet" })}
        </button>
      </div>
    </article>
  );
}

function AddPaymentMethodModal({ onClose, onSubmit, isSubmitting, error }) {
  const intl = useIntl();
  const [form, setForm] = useState({ type: "card", brand: "visa", tokenRef: "", lastFour: "", expMonth: "1", expYear: "2028" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      type: form.type,
      brand: form.brand,
      tokenRef: form.tokenRef,
      lastFour: form.lastFour,
      expMonth: parseInt(form.expMonth, 10),
      expYear: parseInt(form.expYear, 10),
    });
  };

  return (
    <div className="wc-lock-overlay" role="presentation">
      <div className="wc-lock-card is-create" role="dialog" aria-modal="true">
        <button type="button" className="wc-lock-close" aria-label={intl.formatMessage({ id: "dashboard.walletPage.addMethod.close" })} onClick={onClose}>
          <CloseIcon />
        </button>
        <div className="wc-lock-icon-wrap" aria-hidden="true"><PlusIcon /></div>
        <span className="wc-lock-pill"><PlusIcon />{intl.formatMessage({ id: "dashboard.walletPage.addMethod.pill" })}</span>
        <h2>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.title" })}</h2>

        <form className="wc-wallet-create-form" onSubmit={handleSubmit}>
          <label className="wc-wallet-password-field">
            <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.typeLabel" })}</span>
            <select className="wc-currency-select" value={form.type} onChange={(e) => set("type", e.target.value)}>
              <option value="card">{intl.formatMessage({ id: "dashboard.walletPage.addMethod.typeCard" })}</option>
              <option value="mobile_money">{intl.formatMessage({ id: "dashboard.walletPage.addMethod.typeMobileMoney" })}</option>
            </select>
          </label>

          {form.type === "card" ? (
            <>
              <label className="wc-wallet-password-field">
                <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.networkLabel" })}</span>
                <select className="wc-currency-select" value={form.brand} onChange={(e) => set("brand", e.target.value)}>
                  <option value="visa">Visa</option>
                  <option value="mastercard">Mastercard</option>
                  <option value="amex">Amex</option>
                </select>
              </label>
              <label className="wc-wallet-password-field">
                <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.lastFourLabel" })}</span>
                <input className="wc-currency-select" type="text" maxLength={4} placeholder="4242" value={form.lastFour} onChange={(e) => set("lastFour", e.target.value.replace(/\D/g, "").slice(0, 4))} required />
              </label>
              <div className="wc-pm-row">
                <label className="wc-wallet-password-field">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.expMonthLabel" })}</span>
                  <select className="wc-currency-select" value={form.expMonth} onChange={(e) => set("expMonth", e.target.value)}>
                    {[...Array(12)].map((_, i) => <option key={i + 1} value={i + 1}>{String(i + 1).padStart(2, "0")}</option>)}
                  </select>
                </label>
                <label className="wc-wallet-password-field">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.expYearLabel" })}</span>
                  <select className="wc-currency-select" value={form.expYear} onChange={(e) => set("expYear", e.target.value)}>
                    {[2025,2026,2027,2028,2029,2030,2031,2032].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
              </div>
            </>
          ) : (
            <label className="wc-wallet-password-field">
              <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.networkLabel" })}</span>
              <select className="wc-currency-select" value={form.brand} onChange={(e) => set("brand", e.target.value)}>
                <option value="mtn">MTN Mobile Money</option>
                <option value="orange">Orange Money</option>
              </select>
            </label>
          )}

          <label className="wc-wallet-password-field">
            <span>{intl.formatMessage({ id: "dashboard.walletPage.addMethod.tokenLabel" })}</span>
            <input className="wc-currency-select" type="text" placeholder="string" value={form.tokenRef} onChange={(e) => set("tokenRef", e.target.value)} required />
          </label>

          {error ? <div className="wc-lock-feedback is-error">{error}</div> : null}
          <button type="submit" className="wc-create-wallet-btn" disabled={isSubmitting}>
            {isSubmitting ? intl.formatMessage({ id: "dashboard.walletPage.addMethod.submitting" }) : intl.formatMessage({ id: "dashboard.walletPage.addMethod.submit" })}
          </button>
        </form>
      </div>
    </div>
  );
}

function WalletCreateModal({ onClose, onSubmit, isSubmitting, error }) {
  const intl = useIntl();
  const [currency, setCurrency] = useState("XAF");

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(currency);
  };

  return (
    <div className="wc-lock-overlay" role="presentation">
      <div className="wc-lock-card is-create" role="dialog" aria-modal="true">
        <button type="button" className="wc-lock-close" aria-label={intl.formatMessage({ id: "dashboard.walletPage.createWallet.close" })} onClick={onClose}>
          <CloseIcon />
        </button>

        <div className="wc-lock-icon-wrap" aria-hidden="true">
          <PlusIcon />
        </div>

        <span className="wc-lock-pill">
          <PlusIcon />
          {intl.formatMessage({ id: "dashboard.walletPage.createWallet.pill" })}
        </span>

        <h2>{intl.formatMessage({ id: "dashboard.walletPage.createWallet.title" })}</h2>
        <p>{intl.formatMessage({ id: "dashboard.walletPage.createWallet.description" })}</p>

        <form className="wc-wallet-create-form" onSubmit={handleSubmit}>
          <label className="wc-wallet-password-field">
            <span>{intl.formatMessage({ id: "dashboard.walletPage.createWallet.currencyLabel" })}</span>
            <select
              className="wc-currency-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            >
              <option value="XAF">{intl.formatMessage({ id: "dashboard.walletPage.createWallet.currencyXAF" })}</option>
              <option value="EUR">{intl.formatMessage({ id: "dashboard.walletPage.createWallet.currencyEUR" })}</option>
              <option value="USD">{intl.formatMessage({ id: "dashboard.walletPage.createWallet.currencyUSD" })}</option>
            </select>
          </label>

          {error ? <div className="wc-lock-feedback is-error">{error}</div> : null}

          <button type="submit" className="wc-create-wallet-btn" disabled={isSubmitting}>
            {isSubmitting ? intl.formatMessage({ id: "dashboard.walletPage.createWallet.submitting" }) : intl.formatMessage({ id: "dashboard.walletPage.createWallet.submit" })}
          </button>
        </form>
      </div>
    </div>
  );
}

function WalletAccessOverlay({
  feedback = "",
  isSubmitting = false,
  onCloseCreateWallet,
  onOpenCreateWallet,
  onSubmitCreateWallet,
  onTogglePasswordVisibility,
  onWalletPasswordChange,
  showCreateWalletForm = false,
  showPassword = false,
  walletPassword = "",
  walletStatus = "checking",
}) {
  const intl = useIntl();
  const mode = showCreateWalletForm ? "create" : walletStatus;
  const isCreateMode = mode === "create";
  const feedbackTone = String(feedback || "")
    .trim()
    .toLowerCase()
    .startsWith("veuillez")
    ? "is-error"
    : "is-info";
  const title =
    mode === "checking"
      ? intl.formatMessage({ id: "dashboard.walletPage.access.titleChecking" })
      : mode === "pending"
        ? intl.formatMessage({ id: "dashboard.walletPage.access.titlePending" })
        : mode === "approved-needs-creation"
          ? intl.formatMessage({ id: "dashboard.walletPage.access.titleApproved" })
          : mode === "error"
            ? intl.formatMessage({ id: "dashboard.walletPage.access.titleError" })
            : intl.formatMessage({ id: "dashboard.walletPage.access.titleCreate" });
  const description =
    mode === "checking"
      ? intl.formatMessage({ id: "dashboard.walletPage.access.descChecking" })
      : mode === "pending"
        ? intl.formatMessage({ id: "dashboard.walletPage.access.descPending" })
        : mode === "approved-needs-creation"
          ? intl.formatMessage({ id: "dashboard.walletPage.access.descApproved" })
          : mode === "error"
            ? intl.formatMessage({ id: "dashboard.walletPage.access.descError" })
            : intl.formatMessage({ id: "dashboard.walletPage.access.descCreate" });

  return (
    <div className="wc-lock-overlay" role="presentation">
      <div
        className={`wc-lock-card ${isCreateMode ? "is-create" : ""} ${mode === "error" ? "is-error" : ""}`.trim()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="wc-lock-title"
        aria-describedby="wc-lock-description"
      >
        <button
          type="button"
          className="wc-lock-close"
          aria-label={intl.formatMessage({ id: "dashboard.walletPage.access.close" })}
          onClick={isCreateMode ? onCloseCreateWallet : undefined}
        >
          <CloseIcon />
        </button>

        <div className="wc-lock-icon-wrap" aria-hidden="true">
          <LockIcon />
        </div>

        <span className="wc-lock-pill">
          <LockIcon />
          {mode === "checking"
            ? intl.formatMessage({ id: "dashboard.walletPage.access.pillChecking" })
            : mode === "pending"
              ? intl.formatMessage({ id: "dashboard.walletPage.access.pillPending" })
              : mode === "approved-needs-creation"
                ? intl.formatMessage({ id: "dashboard.walletPage.access.pillApproved" })
                : mode === "error"
                  ? intl.formatMessage({ id: "dashboard.walletPage.access.pillError" })
                  : intl.formatMessage({ id: "dashboard.walletPage.access.pillCreate" })}
        </span>

        <h2 id="wc-lock-title">{title}</h2>
        <p id="wc-lock-description">{description}</p>

        {mode === "approved-needs-creation" ? (
          <button type="button" className="wc-create-wallet-btn" onClick={onOpenCreateWallet}>
            <PlusIcon />
            {intl.formatMessage({ id: "dashboard.walletPage.access.createButton" })}
          </button>
        ) : null}

        {isCreateMode ? (
          <form className="wc-wallet-create-form" onSubmit={onSubmitCreateWallet}>
            <label className="wc-wallet-password-field">
              <span>{intl.formatMessage({ id: "dashboard.walletPage.access.passwordLabel" })}</span>
              <div className="wc-wallet-password-input-wrap">
                <input
                  type={showPassword ? "text" : "password"}
                  value={walletPassword}
                  onChange={(event) => onWalletPasswordChange(event.target.value)}
                  placeholder={intl.formatMessage({ id: "dashboard.walletPage.access.passwordPlaceholder" })}
                />
                <button
                  type="button"
                  className="wc-wallet-password-toggle"
                  aria-label={intl.formatMessage({ id: showPassword ? "dashboard.walletPage.access.hidePassword" : "dashboard.walletPage.access.showPassword" })}
                  onClick={onTogglePasswordVisibility}
                >
                  <EyeIcon />
                </button>
              </div>
            </label>

            {feedback ? <div className={`wc-lock-feedback ${feedbackTone}`.trim()}>{feedback}</div> : null}

            <button type="submit" className="wc-create-wallet-btn" disabled={isSubmitting}>
              {isSubmitting ? intl.formatMessage({ id: "dashboard.walletPage.access.confirming" }) : intl.formatMessage({ id: "dashboard.walletPage.access.confirm" })}
            </button>
          </form>
        ) : null}
      </div>
    </div>
  );
}

function InfoCard({ title, value, note, cta, tone, icon, isLoyalty, isCoupons }) {
  const intl = useIntl();

  return (
    <article className="wc-info-card">
      <div className="wc-info-head">
        <span className={`wc-info-icon ${tone}`}>
          {icon === "gift" ? <PlusIcon /> : null}
          {icon === "coupon" ? <CardIcon /> : null}
        </span>
        <div>
          <h3>{title}</h3>
          <span className="wc-info-cta">{cta}</span>
        </div>
      </div>

      <strong>{value}</strong>
      <p>{note}</p>

      {isLoyalty ? (
        <>
          <div className="wc-progress">
            <span />
          </div>
          <div className="wc-progress-note">
            <span>⭐</span>
            <strong>{intl.formatMessage({ id: "dashboard.walletPage.info.loyalty.nextTier" })}</strong>
          </div>
        </>
      ) : null}

      {isCoupons ? (
        <button type="button" className="wc-outline-btn">
          {intl.formatMessage({ id: "dashboard.walletPage.info.coupons.viewButton" })}
        </button>
      ) : null}
    </article>
  );
}

function QuickAction({ action, onClick }) {
  return (
    <button type="button" className="wc-quick-action" onClick={onClick}>
      <span className="wc-quick-action-icon">
        <PlusIcon />
      </span>
      <span className="wc-quick-action-copy">
        <strong>{action.title}</strong>
        <small>{action.desc}</small>
      </span>
      <span className="wc-quick-action-arrow">›</span>
    </button>
  );
}

function TransactionRow({ transaction }) {
  const intl = useIntl();
  const toneClass =
    transaction.tone === "danger"
      ? "is-danger"
      : transaction.tone === "bonus"
        ? "is-bonus"
        : "is-success";

  return (
    <div className="wc-transaction-row">
      <div className={`wc-transaction-icon ${transaction.icon}`}>
        <TransactionIcon icon={transaction.icon} />
      </div>

      <div className="wc-transaction-main">
        <strong>{transaction.title}</strong>
        <span>{transaction.route}</span>
      </div>

      <div className="wc-transaction-date">
        <strong>{transaction.date}</strong>
        <span>{transaction.time}</span>
      </div>

      <div className={`wc-transaction-kind ${toneClass}`}>{transaction.kind}</div>

      <div className="wc-transaction-amount">
        <strong>{transaction.amount} FCFA</strong>
        <span>{transaction.balance}</span>
      </div>

      <button type="button" className="wc-transaction-more" aria-label={intl.formatMessage({ id: "dashboard.walletPage.transactions.moreOptions" })}>
        <span />
        <span />
        <span />
      </button>
    </div>
  );
}

function MethodItem({ item }) {
  return (
    <button type="button" className="wc-method-item">
      <span className={`wc-method-badge ${item.tone}`}>{item.badge}</span>
      <span className="wc-method-copy">
        <strong>{item.brand}</strong>
        <small>{item.number}</small>
      </span>
      <span className="wc-method-arrow">›</span>
    </button>
  );
}

function RechargeWalletModal({ wallet, onClose, onSubmit, isSubmitting, error }) {
  const intl = useIntl();
  const [amountInput, setAmountInput] = useState("25000");
  const [selectedMethod, setSelectedMethod] = useState("orange");

  const rechargePaymentMethods = getRechargePaymentMethods(intl);
  const currentAmount = parseRechargeAmount(amountInput);
  const selectedPreset = RECHARGE_PRESET_AMOUNTS.includes(currentAmount) ? currentAmount : null;
  const walletName = wallet?.name || intl.formatMessage({ id: "dashboard.walletPage.walletName.primary" });
  const walletCurrency = "FCFA";

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      amount: currentAmount,
      paymentMethod: selectedMethod,
    });
  };

  const closeModal = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <div className="wc-recharge-overlay" role="presentation" onClick={closeModal}>
      <div
        className="wc-recharge-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wc-recharge-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="wc-recharge-close" aria-label={intl.formatMessage({ id: "dashboard.walletPage.recharge.close" })} onClick={closeModal}>
          <CloseIcon />
        </button>

        <div className="wc-recharge-header">
          <div>
            <h2 id="wc-recharge-title">{intl.formatMessage({ id: "dashboard.walletPage.recharge.title" })}</h2>
            <p>{intl.formatMessage({ id: "dashboard.walletPage.recharge.subtitle" })}</p>
          </div>
        </div>

        <div className="wc-recharge-balance-card" aria-label={intl.formatMessage({ id: "dashboard.walletPage.recharge.currentBalance" })}>
          <div className="wc-recharge-balance-icon" aria-hidden="true">
            <WalletMiniIcon />
          </div>
          <div className="wc-recharge-balance-copy">
            <span>{intl.formatMessage({ id: "dashboard.walletPage.recharge.currentBalance" })}</span>
            <strong>
              {formatFCFA(wallet?.balance || 0)} {walletCurrency}
            </strong>
            <small>{walletName}</small>
          </div>
        </div>

        <form className="wc-recharge-form" onSubmit={handleSubmit}>
          <div className="wc-recharge-grid">
            <section className="wc-recharge-section wc-recharge-section--amount">
              <div className="wc-recharge-step">
                <span className="wc-recharge-step-badge">1</span>
                <h3>{intl.formatMessage({ id: "dashboard.walletPage.recharge.step1" })}</h3>
              </div>

              <div className="wc-recharge-presets">
                {RECHARGE_PRESET_AMOUNTS.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={`wc-recharge-amount-btn${amount === selectedPreset ? " is-active" : ""}`}
                    onClick={() => setAmountInput(String(amount))}
                  >
                    {formatFCFA(amount)} FCFA
                  </button>
                ))}
              </div>

              <label className="wc-recharge-custom">
                <span className="wc-recharge-label">{intl.formatMessage({ id: "dashboard.walletPage.recharge.customAmountLabel" })}</span>
                <div className="wc-recharge-input-wrap">
                  <input
                    className="wc-recharge-input"
                    type="text"
                    inputMode="numeric"
                    value={formatRechargeAmountInput(amountInput)}
                    onChange={(event) => setAmountInput(event.target.value.replace(/\D/g, ""))}
                    placeholder={intl.formatMessage({ id: "dashboard.walletPage.recharge.customAmountPlaceholder" })}
                  />
                  <span className="wc-recharge-currency">{walletCurrency}</span>
                </div>
              </label>
            </section>

            <section className="wc-recharge-section wc-recharge-section--payment">
              <div className="wc-recharge-step">
                <span className="wc-recharge-step-badge">2</span>
                <h3>{intl.formatMessage({ id: "dashboard.walletPage.recharge.step2" })}</h3>
              </div>

              <div className="wc-recharge-payment-methods">
                {rechargePaymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    className={`wc-recharge-method-card ${method.tone}${selectedMethod === method.id ? " is-active" : ""}`}
                    aria-pressed={selectedMethod === method.id}
                    onClick={() => setSelectedMethod(method.id)}
                  >
                    <span className="wc-recharge-method-radio" aria-hidden="true" />
                    <span className={`wc-recharge-method-logo ${method.id}`} aria-hidden="true">
                      {method.id === "orange" ? (
                        <OrangeMoneyLogo />
                      ) : method.id === "mtn" ? (
                        <MtnMoneyLogo />
                      ) : (
                        <PaypalLogo />
                      )}
                    </span>
                    <span className="wc-recharge-method-copy">
                      <strong>{method.title}</strong>
                      <small>{method.description}</small>
                      <small>{method.helper}</small>
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section className="wc-recharge-section wc-recharge-section--summary">
              <div className="wc-recharge-step">
                <span className="wc-recharge-step-badge">3</span>
                <h3>{intl.formatMessage({ id: "dashboard.walletPage.recharge.step3" })}</h3>
              </div>

              <div className="wc-recharge-summary-box">
                <div className="wc-recharge-summary-row">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.recharge.amount" })}</span>
                  <strong>{currentAmount > 0 ? `${formatFCFA(currentAmount)} FCFA` : "0 FCFA"}</strong>
                </div>
                <div className="wc-recharge-summary-row">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.recharge.fees" })}</span>
                  <strong className="is-free">{intl.formatMessage({ id: "dashboard.walletPage.recharge.free" })}</strong>
                </div>
                <div className="wc-recharge-summary-row is-total">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.recharge.total" })}</span>
                  <strong>{currentAmount > 0 ? `${formatFCFA(currentAmount)} FCFA` : "0 FCFA"}</strong>
                </div>
              </div>
            </section>
          </div>

          {error ? <div className="wc-recharge-error">{error}</div> : null}

          <div className="wc-recharge-actions">
            <div className="wc-recharge-buttons">
              <button type="button" className="wc-recharge-cancel-btn" onClick={closeModal} disabled={isSubmitting}>
                {intl.formatMessage({ id: "dashboard.walletPage.recharge.cancel" })}
              </button>
              <button type="submit" className="wc-recharge-submit-btn" disabled={isSubmitting || currentAmount <= 0}>
                <LockIcon />
                {isSubmitting ? intl.formatMessage({ id: "dashboard.walletPage.recharge.submitting" }) : intl.formatMessage({ id: "dashboard.walletPage.recharge.submit" })}
              </button>
            </div>

            <span className="wc-recharge-note">
              <ShieldIcon />
              {intl.formatMessage({ id: "dashboard.walletPage.recharge.secureNote" })}
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WalletClient() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state?.AuthReducer?.user || {});
  const userId = useSelector(
    (state) => state?.AuthReducer?.userid || state?.AuthReducer?.user?.id || localStorage.getItem("userid") || ""
  );
  const lewooPayToken = useSelector(
    (state) => state?.AuthReducer?.lewooPayToken || localStorage.getItem("lewooPayToken") || ""
  );
  const resolvedToken = String(lewooPayToken || "").trim();
  const { walletLocked, walletLoading, walletStatus } = useLewooPayWalletAccess();

  const quickActions = getQuickActions(intl);

  // LewooPayWallet overlay state
  const [showCreateWalletForm, setShowCreateWalletForm] = useState(false);
  const [walletPassword, setWalletPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [walletFeedback, setWalletFeedback] = useState("");
  const [isSubmittingCreateWallet, setIsSubmittingCreateWallet] = useState(false);
  const submitTimerRef = useRef(null);

  // Client wallets (API)
  const [clientWallets, setClientWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [selectedWalletId, setSelectedWalletId] = useState(null);
  const [syncedBalance, setSyncedBalance] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [isSubmittingRecharge, setIsSubmittingRecharge] = useState(false);
  const [rechargeError, setRechargeError] = useState("");

  // UI state
  const [walletDropdownOpen, setWalletDropdownOpen] = useState(false);
  const [showBalance, setShowBalance] = useState(true);

  // New wallet creation modal
  const [showNewWalletModal, setShowNewWalletModal] = useState(false);
  const [isCreatingWallet, setIsCreatingWallet] = useState(false);
  const [createWalletError, setCreateWalletError] = useState("");

  // Transactions
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // Payment methods
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [showAddMethodModal, setShowAddMethodModal] = useState(false);
  const [isAddingMethod, setIsAddingMethod] = useState(false);
  const [addMethodError, setAddMethodError] = useState("");
  // Load wallet list on mount — guard behind walletStatus "ready" to avoid 403 disconnect
  useEffect(() => {
    if (!resolvedToken || walletStatus !== "ready") {
      setClientWallets([]);
      setSelectedWalletId(null);
      setWalletsLoading(false);
      return undefined;
    }

    setWalletsLoading(true);
    WalletApi.getClientWallets(resolvedToken)
      .then((res) => {
        const wallets = res?.data?.data || [];
        setClientWallets(wallets);
        const primary = wallets.find((w) => w.is_primary) || wallets[0] || null;
        if (primary) setSelectedWalletId(primary.id);
      })
      .catch(() => {})
      .finally(() => setWalletsLoading(false));
  }, [resolvedToken, walletStatus]);

  // Sync balance when selected wallet changes
  useEffect(() => {
    if (!selectedWalletId || !resolvedToken) return undefined;
    setSyncedBalance(null);
    setIsSyncing(true);
    WalletApi.syncClientWallet(selectedWalletId, resolvedToken)
      .then((res) => {
        const data = res?.data?.data || {};
        setSyncedBalance({
          balance: data.balance ?? 0,
          pendingBalance: data.pendingBalance ?? 0,
        });
      })
      .catch(() => {})
      .finally(() => setIsSyncing(false));
  }, [selectedWalletId, resolvedToken]);

  // Load last 4 transactions when selected wallet changes
  useEffect(() => {
    if (!selectedWalletId || !resolvedToken) {
      setWalletTransactions([]);
      return undefined;
    }
    setTransactionsLoading(true);
    WalletApi.getClientWalletTransactions(selectedWalletId, resolvedToken)
      .then((res) => {
        const txs = res?.data?.data || [];
        setWalletTransactions(txs.slice(0, 4));
      })
      .catch(() => setWalletTransactions([]))
      .finally(() => setTransactionsLoading(false));
  }, [selectedWalletId, resolvedToken]);

  // Load payment methods on mount — guard behind walletStatus "ready"
  useEffect(() => {
    if (!resolvedToken || walletStatus !== "ready") return;
    setPaymentMethodsLoading(true);
    WalletApi.getClientPaymentMethods(resolvedToken)
      .then((res) => setPaymentMethods(res?.data?.data || []))
      .catch(() => {})
      .finally(() => setPaymentMethodsLoading(false));
  }, [resolvedToken, walletStatus]);

  const handleAddPaymentMethod = (data) => {
    setIsAddingMethod(true);
    setAddMethodError("");
    WalletApi.addClientPaymentMethod(resolvedToken, data)
      .then((res) => {
        const created = res?.data?.data;
        if (created) setPaymentMethods((prev) => [...prev, created]);
        setShowAddMethodModal(false);
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || intl.formatMessage({ id: "dashboard.walletPage.addMethod.error" });
        setAddMethodError(msg);
      })
      .finally(() => setIsAddingMethod(false));
  };

  // Normalize wallets for display
  const walletsForDropdown = clientWallets.map((w, i) => ({
    id: w.id,
    name: getWalletDisplayName(w, intl),
    color: WALLET_COLORS[i % WALLET_COLORS.length],
    balance: parseFloat(syncedBalance && w.id === selectedWalletId ? syncedBalance.balance : w.balance) || 0,
    balanceFmt: formatFCFA(syncedBalance && w.id === selectedWalletId ? syncedBalance.balance : w.balance),
    currency: w.currency,
  }));

  const rawSelectedWallet = clientWallets.find((w) => w.id === selectedWalletId) || null;
  const primaryClientWallet = clientWallets.find((wallet) => wallet.is_primary) || null;
  const selectedWalletIndex = clientWallets.findIndex((w) => w.id === selectedWalletId);
  const currentBalance = syncedBalance ? syncedBalance.balance : parseFloat(rawSelectedWallet?.balance ?? 0);
  const currentPending = syncedBalance ? syncedBalance.pendingBalance : parseFloat(rawSelectedWallet?.pending_balance ?? 0);

  const walletForTopBar = rawSelectedWallet
    ? {
        id: rawSelectedWallet.id,
        name: getWalletDisplayName(rawSelectedWallet, intl),
        color: WALLET_COLORS[selectedWalletIndex % WALLET_COLORS.length],
        balance: currentBalance,
        balanceFmt: formatFCFA(currentBalance),
        currency: rawSelectedWallet.currency,
      }
    : null;

  // Refresh wallet list (called after create)
  const refreshWallets = () => {
    if (!resolvedToken) {
      return Promise.resolve();
    }

    return WalletApi.getClientWallets(resolvedToken)
      .then((res) => {
        const wallets = res?.data?.data || [];
        setClientWallets(wallets);
      })
      .catch(() => {});
  };

  const handleRechargeSubmit = async ({ amount, paymentMethod }) => {
    const numericAmount = Number(amount) || 0;
    const resolvedUserId = String(userId || authUser?.id || authUser?._id || "").trim();
    const selectedPaymentMethod = String(paymentMethod || "").trim().toLowerCase();

    if (selectedPaymentMethod !== "paypal" && !selectedWalletId) {
      setRechargeError(intl.formatMessage({ id: "dashboard.walletPage.errors.noWalletSelected" }));
      return;
    }

    if (selectedPaymentMethod !== "paypal" && !resolvedUserId) {
      setRechargeError(intl.formatMessage({ id: "dashboard.walletPage.errors.missingUserId" }));
      return;
    }

    if (numericAmount <= 0) {
      setRechargeError(intl.formatMessage({ id: "dashboard.walletPage.errors.invalidAmount" }));
      return;
    }

    setIsSubmittingRecharge(true);
    setRechargeError("");
    dispatch(showLoader(selectedPaymentMethod === "paypal"
      ? intl.formatMessage({ id: "dashboard.walletPage.loaders.paypalPreparing" })
      : intl.formatMessage({ id: "dashboard.walletPage.loaders.recharging" })));

    try {
      if (selectedPaymentMethod === "paypal") {
        const walletIdForPaypal = primaryClientWallet?.id || selectedWalletId;

        if (!walletIdForPaypal) {
          throw new Error(intl.formatMessage({ id: "dashboard.walletPage.errors.noPrimaryWalletPaypal" }));
        }

        const res = await WalletApi.createPaypalOrder(resolvedToken, {
          wallet_id: walletIdForPaypal,
          amount: numericAmount,
          currency: "USD",
          return_url: `${window.location.origin}/paiement-success?amount=${numericAmount}&currency=FCFA&from=wallet-client`,
          cancel_url: `${window.location.origin}/paiement-cancel?amount=${numericAmount}&currency=FCFA&from=wallet-client`,
        });

        const orderData = res?.data?.data || {};
        const approveLink = orderData.approve_link || orderData.approval_url || orderData.url || "";

        if (!approveLink) {
          throw new Error(intl.formatMessage({ id: "dashboard.walletPage.errors.paypalLinkMissing" }));
        }

        setShowRechargeModal(false);
        window.location.assign(approveLink);
        return;
      }

      await WalletApi.addwalletmoney({
        creditedAmount: numericAmount,
        walletId: selectedWalletId,
        userId: resolvedUserId,
      });

      setShowRechargeModal(false);

      await refreshWallets();

      if (selectedWalletId && resolvedToken) {
        try {
          const syncRes = await WalletApi.syncClientWallet(selectedWalletId, resolvedToken);
          const syncData = syncRes?.data?.data || null;
          if (syncData) {
            setSyncedBalance({
              balance: syncData.balance ?? 0,
              pendingBalance: syncData.pendingBalance ?? 0,
            });
          }
        } catch (syncError) {
          // Keep the recharge success visible even if sync fails silently.
        }

        try {
          const transactionsRes = await WalletApi.getClientWalletTransactions(selectedWalletId, resolvedToken);
          const txs = transactionsRes?.data?.data || [];
          setWalletTransactions(txs.slice(0, 4));
        } catch (txError) {
          // Non-blocking refresh.
        }
      }
    } catch (err) {
      setRechargeError(err?.response?.data?.message || err?.message || intl.formatMessage({ id: "dashboard.walletPage.errors.rechargeGeneric" }));
    } finally {
      setIsSubmittingRecharge(false);
      dispatch(hideLoader());
    }
  };

  const handleCreateNewWallet = (currency) => {
    setIsCreatingWallet(true);
    setCreateWalletError("");
    WalletApi.createClientWallet(resolvedToken, { currency })
      .then((res) => {
        const newWallet = res?.data?.data || null;
        setShowNewWalletModal(false);
        refreshWallets();
        if (newWallet?.id) setSelectedWalletId(newWallet.id);
      })
      .catch((err) => {
        setCreateWalletError(
          err?.response?.data?.message || intl.formatMessage({ id: "dashboard.walletPage.errors.createWalletGeneric" })
        );
      })
      .finally(() => setIsCreatingWallet(false));
  };

  // LewooPayWallet handlers
  useEffect(() => {
    if (walletStatus !== "approved-needs-creation") {
      setShowCreateWalletForm(false);
    }
    if (walletStatus === "ready") {
      setShowCreateWalletForm(false);
      setWalletPassword("");
      setShowPassword(false);
      setWalletFeedback("");
      setIsSubmittingCreateWallet(false);
    }
  }, [walletStatus]);

  useEffect(
    () => () => {
      if (submitTimerRef.current) {
        window.clearTimeout(submitTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!showRechargeModal) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setShowRechargeModal(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [showRechargeModal]);

  const clearCreateWalletTimer = () => {
    if (submitTimerRef.current) {
      window.clearTimeout(submitTimerRef.current);
      submitTimerRef.current = null;
    }
  };

  const openCreateWalletForm = () => {
    clearCreateWalletTimer();
    setShowCreateWalletForm(true);
    setWalletFeedback("");
  };

  const closeCreateWalletForm = () => {
    clearCreateWalletTimer();
    setShowCreateWalletForm(false);
    setWalletPassword("");
    setShowPassword(false);
    setWalletFeedback("");
    setIsSubmittingCreateWallet(false);
  };

  const handleCreateWalletSubmit = (event) => {
    event.preventDefault();

    const password = String(walletPassword || "").trim();
    if (!password) {
      setWalletFeedback(intl.formatMessage({ id: "dashboard.walletPage.errors.passwordRequired" }));
      return;
    }

    const email = String(authUser?.email || "").trim();
    const firstName = String(authUser?.firstName || authUser?.first_name || "").trim();
    const lastName = String(authUser?.lastName || authUser?.last_name || "").trim();
    const phone = String(authUser?.phone || authUser?.mobile_number || "").trim();

    if (!email || !firstName || !lastName || !phone) {
      setWalletFeedback(intl.formatMessage({ id: "dashboard.walletPage.errors.profileInfoMissing" }));
      return;
    }

    setIsSubmittingCreateWallet(true);
    setWalletFeedback("");

    void (async () => {
      try {
        const response = await WalletApi.createLewooPayWallet({
          country: "CM",
          currency: "XAF",
          email,
          firstName,
          lastName,
          password,
          phone,
          role: "consumer",
        });

        const responseData = response?.data?.data || response?.data || {};
        const lewooPayToken =
          responseData?.lewooPay?.token ||
          responseData?.lewooPayToken ||
          responseData?.token ||
          responseData?.accessToken ||
          "";
        const lewooPaySession = responseData?.lewooPay?.session || responseData?.lewooPaySession || null;

        if (lewooPayToken || lewooPaySession) {
          dispatch({
            type: ACTION_TYPES.LEWOO_PAY_UPDATE,
            payload: { lewooPaySession, lewooPayToken },
          });
        }

        dispatch({ type: ACTION_TYPES.LEWOO_PAY_ACCESS_REFRESH });
        setWalletFeedback(intl.formatMessage({ id: "dashboard.walletPage.success.lewooWalletCreated" }));
      } catch (error) {
        setWalletFeedback(
          error?.response?.data?.message ||
            intl.formatMessage({ id: "dashboard.walletPage.errors.lewooCreateFailed" })
        );
      } finally {
        setIsSubmittingCreateWallet(false);
      }
    })();
  };

  return (
    <>
      <div className={`wc-shell ${walletLocked ? "is-locked" : ""}`}>
        <div
          className="wc-page-content"
          inert={walletLocked ? "" : undefined}
          aria-hidden={walletLocked ? "true" : undefined}
        >
          <div className="wc-hero">
            <div>
              <h1>{intl.formatMessage({ id: "dashboard.walletPage.hero.title" })}</h1>
              <p>{intl.formatMessage({ id: "dashboard.walletPage.hero.subtitle" })}</p>
            </div>
          </div>

          <section className="wc-top-grid">
            <WalletTopBar
              wallet={walletForTopBar}
              wallets={walletsForDropdown}
              showBalance={showBalance}
              isSyncing={isSyncing}
              loading={walletsLoading}
              onToggleBalance={() => setShowBalance((v) => !v)}
              onOpenDropdown={() => setWalletDropdownOpen(true)}
              dropdownOpen={walletDropdownOpen}
              onSelect={(id) => { setSelectedWalletId(id); setSyncedBalance(null); }}
              onClose={() => setWalletDropdownOpen(false)}
              onCreateWallet={() => { setCreateWalletError(""); setShowNewWalletModal(true); }}
            />

            <InfoCard
              title={intl.formatMessage({ id: "dashboard.walletPage.info.loyalty.title" })}
              value={intl.formatMessage({ id: "dashboard.walletPage.info.loyalty.value" })}
              note={intl.formatMessage({ id: "dashboard.walletPage.info.loyalty.note" })}
              cta={intl.formatMessage({ id: "dashboard.walletPage.info.loyalty.cta" })}
              tone="green"
              icon="gift"
              isLoyalty
            />

            <InfoCard
              title={intl.formatMessage({ id: "dashboard.walletPage.info.coupons.title" })}
              value={intl.formatMessage({ id: "dashboard.walletPage.info.coupons.value" })}
              note={intl.formatMessage({ id: "dashboard.walletPage.info.coupons.note" })}
              cta={intl.formatMessage({ id: "dashboard.walletPage.info.coupons.cta" })}
              tone="violet"
              icon="coupon"
              isCoupons
            />
          </section>

          <section className="wc-middle-grid">
            <article className="wc-panel">
              <div className="wc-panel-head">
                <h2>{intl.formatMessage({ id: "dashboard.walletPage.quickActionsPanel.title" })}</h2>
              </div>
              <div className="wc-actions-grid">
                {quickActions.map((action) => (
                  <QuickAction
                    key={action.title}
                    action={action}
                    onClick={
                      action.title === intl.formatMessage({ id: "dashboard.walletPage.quickActions.recharge.title" })
                        ? () => {
                            setRechargeError("");
                            setShowRechargeModal(true);
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </article>

            <aside className="wc-summary-panel">
              <div className="wc-panel-head wc-summary-head">
                <span className="wc-summary-badge">
                  <CardIcon />
                </span>
                <h2>{intl.formatMessage({ id: "dashboard.walletPage.summary.title" })}</h2>
              </div>
              <div className="wc-summary-list">
                <div className="wc-summary-row">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.summary.available" })}</span>
                  <strong>{formatFCFA(currentBalance)} FCFA</strong>
                </div>
                <div className="wc-summary-row">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.summary.pending" })}</span>
                  <strong>{formatFCFA(currentPending)} FCFA</strong>
                </div>
                <div className="wc-summary-row is-total">
                  <span>{intl.formatMessage({ id: "dashboard.walletPage.summary.total" })}</span>
                  <strong>{formatFCFA(currentBalance + currentPending)} FCFA</strong>
                </div>
              </div>
            </aside>
          </section>

          <section className="wc-bottom-grid">
            <article className="wc-panel wc-transactions-panel">
              <div className="wc-panel-head">
                <h2>{intl.formatMessage({ id: "dashboard.walletPage.transactions.title" })}</h2>
                <a href="/">{intl.formatMessage({ id: "dashboard.walletPage.transactions.viewAll" })}</a>
              </div>
              <div className="wc-transactions-list">
                {transactionsLoading ? (
                  <p className="wc-transactions-state">{intl.formatMessage({ id: "dashboard.walletPage.transactions.loading" })}</p>
                ) : walletTransactions.length === 0 ? (
                  <p className="wc-transactions-state">{intl.formatMessage({ id: "dashboard.walletPage.transactions.empty" })}</p>
                ) : (
                  walletTransactions.map((t) => (
                    <TransactionRow key={t.id} transaction={normalizeTransaction(t, intl)} />
                  ))
                )}
              </div>
            </article>

            <aside className="wc-panel wc-methods-panel">
              <div className="wc-panel-head">
                <h2>{intl.formatMessage({ id: "dashboard.walletPage.methods.title" })}</h2>
                {paymentMethods.length > 3 ? <a href="/">{intl.formatMessage({ id: "dashboard.walletPage.methods.viewAll" })}</a> : null}
              </div>
              <div className="wc-methods-list">
                {paymentMethodsLoading ? (
                  <p className="wc-transactions-state">{intl.formatMessage({ id: "dashboard.walletPage.methods.loading" })}</p>
                ) : paymentMethods.length === 0 ? (
                  <p className="wc-transactions-state">{intl.formatMessage({ id: "dashboard.walletPage.methods.empty" })}</p>
                ) : (
                  paymentMethods.slice(0, 3).map((pm) => {
                    const item = normalizePaymentMethod(pm);
                    return <MethodItem key={item.id} item={item} />;
                  })
                )}
              </div>
              <button type="button" className="wc-add-method-btn" onClick={() => { setAddMethodError(""); setShowAddMethodModal(true); }}>
                <PlusIcon />
                {intl.formatMessage({ id: "dashboard.walletPage.methods.addButton" })}
              </button>
            </aside>
          </section>
        </div>

        {walletLocked ? (
          <WalletAccessOverlay
            feedback={walletFeedback}
            isSubmitting={isSubmittingCreateWallet}
            onCloseCreateWallet={closeCreateWalletForm}
            onOpenCreateWallet={openCreateWalletForm}
            onSubmitCreateWallet={handleCreateWalletSubmit}
            onTogglePasswordVisibility={() => setShowPassword((value) => !value)}
            onWalletPasswordChange={setWalletPassword}
            showCreateWalletForm={showCreateWalletForm}
            showPassword={showPassword}
            walletPassword={walletPassword}
            walletStatus={walletLoading ? "checking" : walletStatus}
          />
        ) : null}

        {showNewWalletModal ? (
          <WalletCreateModal
            onClose={() => setShowNewWalletModal(false)}
            onSubmit={handleCreateNewWallet}
            isSubmitting={isCreatingWallet}
            error={createWalletError}
          />
        ) : null}

        {showAddMethodModal ? (
          <AddPaymentMethodModal
            onClose={() => setShowAddMethodModal(false)}
            onSubmit={handleAddPaymentMethod}
            isSubmitting={isAddingMethod}
            error={addMethodError}
          />
        ) : null}

        {showRechargeModal ? (
          <RechargeWalletModal
            wallet={walletForTopBar}
            onClose={() => {
              if (!isSubmittingRecharge) {
                setShowRechargeModal(false);
                setRechargeError("");
              }
            }}
            onSubmit={handleRechargeSubmit}
            isSubmitting={isSubmittingRecharge}
            error={rechargeError}
          />
        ) : null}
      </div>
    </>
  );
}